import { DEFAULT_LOCALE, LOCALE_DIRECTIONS } from '@/constants/locales';
import { type PortalDeposit } from '@/schemas/cashierSession.schema';

import { depositsEqual, walletInstructionsEqual } from './equality';
import { type Action, type SessionState } from './types';

export const initialSessionState: SessionState = {
  token: null,
  deposit: null,
  walletInstructions: null,
  phase: 'idle',
  isRefreshing: false,
  isChoosing: false,
  isSubmittingProof: false,
  error: null,
  lastSyncedAt: null,
  lastRequestId: null,
  branding: null,
  effectiveScheme: 'light',
  brandingApplied: false,
  tiles: null,
  tilesLoadState: 'idle',
  tilesError: null,
  clientIntent: { step: 'none' },
  banners: { offline: false, refreshedAfterConflict: false },
  locale: DEFAULT_LOCALE,
  dir: LOCALE_DIRECTIONS[DEFAULT_LOCALE],
};

function depositFromSession(session: {
  id: string;
  publicId: string;
  merchantId: string;
  traderId: string | null;
  playerRef: string;
  status: string;
  mode: PortalDeposit['mode'];
  amount: string;
  receivedAmount: string | null;
  currency: string;
  decimalPlaces: number;
  merchantOrderId: string | null;
  paymentMethodCode: string | null;
  providerReference: string | null;
  createdAt: string;
  expiresAt: string | null;
  verificationExpiresAt: string | null;
  updatedAt: string;
  returnUrl: string | null;
}): PortalDeposit {
  const {
    id,
    publicId,
    merchantId,
    traderId,
    playerRef,
    status,
    mode,
    amount,
    receivedAmount,
    currency,
    decimalPlaces,
    merchantOrderId,
    paymentMethodCode,
    providerReference,
    createdAt,
    expiresAt,
    verificationExpiresAt,
    updatedAt,
    returnUrl,
  } = session;
  return {
    id,
    publicId,
    merchantId,
    traderId,
    playerRef,
    status,
    mode,
    amount,
    receivedAmount,
    currency,
    decimalPlaces,
    merchantOrderId,
    paymentMethodCode,
    providerReference,
    createdAt,
    expiresAt,
    verificationExpiresAt,
    updatedAt,
    returnUrl,
  };
}

function intentAfterStatus(
  previous: SessionState,
  nextStatus: string,
): SessionState['clientIntent'] {
  if (previous.deposit?.status !== nextStatus) return { step: 'none' };
  return previous.clientIntent;
}

export function sessionReducer(state: SessionState, action: Action): SessionState {
  switch (action.type) {
    case 'RESOLVE_STARTED':
      return {
        ...state,
        token: action.token,
        phase: 'resolving',
        error: null,
      };

    case 'RESOLVE_SUCCEEDED': {
      const deposit = depositFromSession(action.session);
      return {
        ...state,
        phase: 'resolved',
        deposit,
        branding: action.session.branding,
        walletInstructions: action.session.walletInstructions,
        lastSyncedAt: action.at,
        lastRequestId: action.requestId ?? state.lastRequestId,
        error: null,
        isRefreshing: false,
        clientIntent: intentAfterStatus(state, deposit.status),
      };
    }

    case 'RESOLVE_FAILED':
      return {
        ...state,
        phase: 'idle',
        error: action.error,
        isRefreshing: false,
      };

    case 'REFRESH_STARTED':
      if (state.isRefreshing) return state;
      return { ...state, isRefreshing: true };

    case 'SESSION_SYNCED': {
      if (
        depositsEqual(state.deposit, action.deposit) &&
        walletInstructionsEqual(state.walletInstructions, action.walletInstructions) &&
        !state.isRefreshing
      ) {
        return state;
      }
      return {
        ...state,
        deposit: action.deposit,
        walletInstructions: action.walletInstructions,
        lastSyncedAt: action.at,
        lastRequestId: action.requestId ?? state.lastRequestId,
        isRefreshing: false,
        error: null,
        clientIntent: intentAfterStatus(state, action.deposit.status),
      };
    }

    case 'REFRESH_FAILED':
      return { ...state, isRefreshing: false, error: action.error };

    case 'METHODS_LOAD_STARTED':
      return { ...state, tilesLoadState: 'loading', tilesError: null };

    case 'METHODS_LOAD_SUCCEEDED':
      return {
        ...state,
        tiles: action.tiles,
        tilesLoadState: 'loaded',
        tilesError: null,
        clientIntent: action.clientIntent ?? state.clientIntent,
      };

    case 'METHODS_LOAD_FAILED':
      return { ...state, tilesLoadState: 'error', tilesError: action.error };

    case 'CHOOSE_STARTED':
      return { ...state, isChoosing: true, error: null };

    case 'CHOOSE_SUCCEEDED':
      return {
        ...state,
        isChoosing: false,
        deposit: action.deposit,
        lastSyncedAt: action.at,
        error: null,
        clientIntent: { step: 'none' },
      };

    case 'CHOOSE_WALLET_SUCCEEDED':
      return {
        ...state,
        isChoosing: false,
        deposit: action.deposit,
        walletInstructions: action.walletInstructions,
        lastSyncedAt: action.at,
        lastRequestId: action.requestId ?? state.lastRequestId,
        error: null,
        clientIntent: { step: 'none' },
      };

    case 'CHOOSE_FAILED': {
      // A 422 keeps the player on the method catalogue. Drop the payer-wallet intent so clearing
      // the error (Continue, poll sync, or a wallet retry) cannot resurrect the failed method.
      const dropIntent = action.error.status === 422;
      return {
        ...state,
        isChoosing: false,
        error: action.error,
        ...(dropIntent ? { clientIntent: { step: 'none' as const } } : {}),
      };
    }

    case 'CHOOSE_ABORTED':
      if (!state.isChoosing) return state;
      return { ...state, isChoosing: false };

    case 'PROOF_SUBMIT_STARTED':
      return { ...state, isSubmittingProof: true, error: null };

    case 'PROOF_SUBMIT_SUCCEEDED':
      return {
        ...state,
        isSubmittingProof: false,
        deposit: action.deposit,
        lastSyncedAt: action.at,
        error: null,
        clientIntent: { step: 'none' },
      };

    case 'PROOF_SUBMIT_FAILED':
      return {
        ...state,
        isSubmittingProof: false,
        error: action.error,
      };

    case 'CLIENT_INTENT_SET':
      return { ...state, clientIntent: action.intent };

    case 'ERROR_CLEARED':
      if (state.error === null) return state;
      return { ...state, error: null };

    case 'OFFLINE_SET':
      if (state.banners.offline === action.offline) return state;
      return { ...state, banners: { ...state.banners, offline: action.offline } };

    case 'CONFLICT_REFRESHED':
      if (state.banners.refreshedAfterConflict) return state;
      return {
        ...state,
        banners: { ...state.banners, refreshedAfterConflict: true },
      };

    case 'BRANDING_APPLIED':
      return { ...state, brandingApplied: true, effectiveScheme: action.scheme };

    case 'SCHEME_SET':
      if (state.effectiveScheme === action.scheme) return state;
      return { ...state, effectiveScheme: action.scheme };

    case 'LOCALE_SET':
      if (state.locale === action.locale && state.dir === action.dir) return state;
      return { ...state, locale: action.locale, dir: action.dir };

    default: {
      const _never: never = action;
      return _never;
    }
  }
}
